import { useCallback, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, Tag } from 'lucide-react';
import { matchingAPI } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';
import { useJobPolling, useJobResume, JobProgressLine, JobErrorBlock } from './JobPollingPanel';

export function ConfirmedMatchCategoryBackfillPanel() {
  const [preview, setPreview] = useState(null);
  const [previewStarting, setPreviewStarting] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(null);

  const [applyStarting, setApplyStarting] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [applyFailed, setApplyFailed] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);

  const previewPoll = useJobPolling({
    fetchStatus: matchingAPI.categoryBackfillJobStatus,
    onCompleted: useCallback((result) => {
      setPreview(result);
      setPreviewFailed(null);
    }, []),
    onFailed: useCallback((error) => {
      setPreviewFailed(error);
    }, []),
  });

  const applyPoll = useJobPolling({
    fetchStatus: matchingAPI.categoryBackfillJobStatus,
    onCompleted: useCallback((result) => {
      setApplyResult(result);
      setApplyFailed(null);
      setApplyProgress(null);
    }, []),
    onFailed: useCallback((error) => {
      setApplyFailed(error);
      setApplyProgress(null);
    }, []),
    onProgress: useCallback((progress) => {
      setApplyProgress(progress);
    }, []),
  });

  // Pick up an already-running Preview/Apply job on page load/refresh (e.g.
  // another tab or a page reload mid-run) so the buttons stay correctly
  // disabled and results aren't lost.
  useJobResume({ fetchLatest: matchingAPI.previewCategoryBackfillJobLatest, poll: previewPoll });
  useJobResume({ fetchLatest: matchingAPI.categoryBackfillJobLatest, poll: applyPoll });

  const runPreview = async () => {
    setPreviewStarting(true);
    setPreviewFailed(null);
    setApplyResult(null);
    setApplyFailed(null);
    try {
      const { data } = await matchingAPI.previewCategoryBackfillStart();
      if (data.job_id) {
        previewPoll.start(data.job_id);
      }
    } catch (err) {
      setPreviewFailed(getErrorMessage(err) || 'Failed to start preview.');
    } finally {
      setPreviewStarting(false);
    }
  };

  const previewBusy = previewStarting || previewPoll.polling;

  const runApply = async () => {
    if (!window.confirm(
      `Stamp category on ${preview?.candidate_count || 0} transaction(s)? ` +
      'Each is categorized (or re-categorized) per its confirmed match (recurring cost, statement payout, outflow invoice, or BR OS client invoice).'
    )) return;
    setApplyStarting(true);
    setApplyFailed(null);
    setApplyResult(null);
    setApplyProgress(null);
    try {
      const { data } = await matchingAPI.applyCategoryBackfillStart();
      if (data.job_id) {
        applyPoll.start(data.job_id);
      }
    } catch (err) {
      setApplyFailed(getErrorMessage(err) || 'Failed to start apply.');
    } finally {
      setApplyStarting(false);
    }
  };

  const applyBusy = applyStarting || applyPoll.polling;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Confirmed Match Category Backfill</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Stamps category on already-reconciled transactions (category Other/unset) that carry a
          confirmed recurring-cost, statement-payout, outflow-invoice, or BR OS client invoice
          (Revenue) match from before the shared confirm-time categorization helper existed. Also
          re-stamps "revenue" rows a statement-payout match previously stamped under the old mapping
          to "Rev - Mgmt Statement". Two-step: Preview, then Apply. Apply runs in the background and
          this panel polls for progress -- it is safe to navigate away and come back.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={runPreview} disabled={previewBusy} data-testid="category-backfill-preview">
            {previewBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Preview
          </Button>
          {preview && !preview.error && preview.candidate_count > 0 && (
            <Button
              variant="default"
              size="sm"
              onClick={runApply}
              disabled={applyBusy}
              data-testid="category-backfill-apply"
            >
              {applyBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Apply to {preview.candidate_count} Transactions
            </Button>
          )}
        </div>

        <JobErrorBlock error={previewFailed} testId="category-backfill-preview-error" />

        {preview && (
          <div className="space-y-2" data-testid="category-backfill-preview-result">
            <div className="flex flex-wrap gap-3 text-xs">
              <span>Transactions to stamp: <strong>{preview.candidate_count}</strong></span>
              {Object.entries(preview.per_category || {}).map(([cat, count]) => (
                <Badge key={cat} variant="outline" className="text-[10px]">
                  {cat}: {count}
                </Badge>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{preview.message}</p>
          </div>
        )}

        {applyPoll.polling && (
          <JobProgressLine progress={applyProgress} testId="category-backfill-apply-progress">
            {(p) => (
              <>
                Stamped {p.stamped ?? 0} of {p.total ?? '?'}
                {p.scanned != null ? ` (scanned ${p.scanned})` : ''}
              </>
            )}
          </JobProgressLine>
        )}

        <JobErrorBlock error={applyFailed} testId="category-backfill-apply-error" />

        {applyResult && !applyFailed && (
          <div className="p-3 rounded text-xs border bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-900" data-testid="category-backfill-apply-result">
            <div className="space-y-1">
              <p><strong>Stamped:</strong> {applyResult.applied} of {applyResult.candidate_count}</p>
              {applyResult.skipped && applyResult.skipped.length > 0 && (
                <p className="text-amber-600"><strong>Skipped:</strong> {applyResult.skipped.length} (see skipped[] for reasons)</p>
              )}
              <div className="flex flex-wrap gap-1">
                {Object.entries(applyResult.per_category || {}).map(([cat, count]) => (
                  <Badge key={cat} variant="outline" className="text-[10px]">
                    {cat}: {count}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
